import { useCallback, useState, useMemo, type FC } from "react";
import type { PlainServiceAccount } from "@/queries/service-account-queries";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/podkit/tables/Table";
import { DeleteServiceAccountDialog } from "./DeleteServiceAccountDialog";
import { EditServiceAccountDialog } from "./EditServiceAccountDialog";
import { ServiceAccountActionsDropdown } from "./ServiceAccountActionsDropdown";
import { useMembers, type PlainOrganizationMember } from "@/queries/organization-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";

type ServiceAccountsListProps = {
    serviceAccounts: PlainServiceAccount[];
};

export const ServiceAccountsList: FC<ServiceAccountsListProps> = ({ serviceAccounts }) => {
    const { data: membersData } = useMembers();
    const members = membersData?.members || [];

    return (
        <Table data-testid="service-accounts-table" className="mt-4">
            <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                <TableRow className="h-8 text-base">
                    <TableHead className="pl-3">Name</TableHead>
                    <TableHead className="pl-3">Description</TableHead>
                    <TableHead className="pl-3">Created by</TableHead>
                    <TableHead className="pl-3">Created on</TableHead>
                    <TableHead className="pl-3">Expires on</TableHead>
                    <TableHead />
                </TableRow>
            </TableHeader>
            <TableBody className="border-t border-border-light text-base">
                {serviceAccounts.map((serviceAccount) => (
                    <ServiceAccountRow key={serviceAccount.id} serviceAccount={serviceAccount} members={members} />
                ))}
            </TableBody>
        </Table>
    );
};

type ServiceAccountRowProps = {
    serviceAccount: PlainServiceAccount;
    members: PlainOrganizationMember[];
};

const ServiceAccountRow: FC<ServiceAccountRowProps> = ({ serviceAccount, members }) => {
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    const creator = useMemo(() => {
        return members.find((m) => m.userId === serviceAccount.creator?.id);
    }, [members, serviceAccount.creator?.id]);

    const handleEdit = useCallback(() => {
        setShowEditDialog(true);
    }, []);

    const handleDelete = useCallback(() => {
        setShowDeleteDialog(true);
    }, []);

    return (
        <>
            <TableRow className="h-16 border-border-light">
                <TableCell className="max-w-xs">
                    <div className="inline-flex grow items-center space-x-3">
                        <ServiceAccountAvatar id={serviceAccount.id} size={32} />
                        <span className="truncate font-medium text-content-primary">{serviceAccount.name}</span>
                    </div>
                </TableCell>
                <TableCell className="max-w-md">
                    <span className="truncate text-content-secondary">{serviceAccount.description || "—"}</span>
                </TableCell>
                <TableCell>
                    {creator ? (
                        <div className="flex items-center gap-2">
                            <Avatar size={24}>
                                {creator.avatarUrl && (
                                    <AvatarImage src={creator.avatarUrl} alt={`${creator.fullName}'s avatar`} />
                                )}
                                <AvatarFallback>
                                    <AvatarInitials name={creator.fullName} size={24} />
                                </AvatarFallback>
                            </Avatar>
                            <span className="text-content-secondary">{creator.fullName}</span>
                        </div>
                    ) : (
                        <span className="text-content-secondary">—</span>
                    )}
                </TableCell>
                <TableCell>
                    <span className="text-content-secondary">
                        {serviceAccount.createdAt
                            ? new Timestamp(serviceAccount.createdAt).toDate().toLocaleDateString()
                            : "—"}
                    </span>
                </TableCell>
                <TableCell>
                    <span className="text-content-secondary">
                        {serviceAccount.validUntil
                            ? new Timestamp(serviceAccount.validUntil).toDate().toLocaleDateString()
                            : "—"}
                    </span>
                </TableCell>
                <TableCell className="w-8">
                    <ServiceAccountActionsDropdown
                        serviceAccount={serviceAccount}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                </TableCell>
            </TableRow>
            <EditServiceAccountDialog
                open={showEditDialog}
                onClose={() => setShowEditDialog(false)}
                serviceAccount={serviceAccount}
            />
            <DeleteServiceAccountDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                serviceAccount={serviceAccount}
            />
        </>
    );
};
